// Ch78 · BULK ACTIONS on the Leads work list — pure orchestration, no React.
//
// A bulk bar that reports "3 selected · Claim" and then silently does nothing
// to two of them is worse than no bulk bar. So this module answers two
// questions out loud:
//
//   WHAT CAN THIS ACTION ACTUALLY TOUCH? Not every selected row is eligible.
//   Claiming is a thing you do to an UNCLAIMED LEAD — a client is already
//   somebody's, and a claimed lead already has an owner. Archiving is a thing
//   you do to a LEAD ROW; there is no such column on a client. The bar shows
//   the eligible count beside each verb and disables what applies to nobody.
//
//   WHAT HAPPENED TO EACH ONE? Rows are attempted one at a time and every
//   failure is kept with its reason (an ownership conflict names the owner),
//   so the summary can say "3 claimed · 1 failed" instead of a green tick over
//   a partial write.

import type { WorkRow } from "./leadsWork";

export type BulkAction = "claim" | "assign" | "archive" | "restore";

export const BULK_LABEL: Record<BulkAction, string> = {
  claim: "Claim",
  assign: "Assign to…",
  archive: "Archive",
  restore: "Restore",
};

/** Claiming and assigning both take ownership of a lead nobody holds yet. */
export function isClaimable(row: WorkRow): boolean {
  return row.person.kind === "lead" && (row.stage === "unclaimed" || row.stage === "expired");
}

/** `leads.status = 'archived'`. A client has no such column and is never touched. */
export function isArchivable(row: WorkRow): boolean {
  return row.person.kind === "lead" && row.stage !== "archived";
}

/** The way BACK. Only a lead that is actually archived can be restored. */
export function isRestorable(row: WorkRow): boolean {
  return row.person.kind === "lead" && row.stage === "archived";
}

export function eligibleFor(rows: WorkRow[], action: BulkAction): WorkRow[] {
  if (action === "archive") return rows.filter(isArchivable);
  if (action === "restore") return rows.filter(isRestorable);
  return rows.filter(isClaimable);
}

export interface BulkOutcome {
  key: string;
  name: string;
  ok: boolean;
  /** Why it failed, in the words the caller can act on. */
  message?: string;
}

export interface BulkResult {
  action: BulkAction;
  /** How many of the selection this action could even apply to. */
  attempted: number;
  /** Selected rows this action does not apply to; named, not silently dropped. */
  skipped: number;
  succeeded: number;
  failures: BulkOutcome[];
  summary: string;
}

export interface BulkExecutors {
  claim: (row: WorkRow, ownerId?: string) => Promise<void>;
  archive: (row: WorkRow) => Promise<void>;
  /** Puts an archived lead back where it can be worked. */
  restore: (row: WorkRow) => Promise<void>;
}

function describe(result: Omit<BulkResult, "summary">): string {
  const verb =
    result.action === "archive"
      ? "archived"
      : result.action === "restore"
        ? "restored"
        : result.action === "assign"
          ? "assigned"
          : "claimed";
  const parts: string[] = [];
  if (result.succeeded > 0) parts.push(`${result.succeeded} ${verb}`);
  if (result.failures.length > 0) {
    // Name the first reason: a bulk run that fails for one reason usually fails
    // for one reason, and a bare count teaches nobody anything.
    parts.push(`${result.failures.length} failed — ${result.failures[0].message ?? "unknown error"}`);
  }
  if (result.skipped > 0) {
    parts.push(`${result.skipped} skipped (this action does not apply to them)`);
  }
  if (parts.length === 0) {
    const noun =
      result.action === "archive" ? "archive" : result.action === "restore" ? "restore" : "claim";
    return `Nothing to ${noun}.`;
  }
  return parts.join(" · ");
}

/**
 * Run `action` over `rows`, one at a time, keeping every outcome.
 *
 * Sequential on purpose: these are ownership writes against one ledger, and a
 * burst of parallel claims is how you turn one operator's click into a race
 * with another's. Slower and legible beats fast and ambiguous.
 */
export async function runBulk(
  rows: WorkRow[],
  action: BulkAction,
  exec: BulkExecutors,
  ownerId?: string,
): Promise<BulkResult> {
  const eligible = eligibleFor(rows, action);
  const failures: BulkOutcome[] = [];
  let succeeded = 0;
  for (const row of eligible) {
    try {
      if (action === "archive") await exec.archive(row);
      else if (action === "restore") await exec.restore(row);
      else await exec.claim(row, action === "assign" ? ownerId : undefined);
      succeeded += 1;
    } catch (error) {
      failures.push({
        key: row.key,
        name: row.person.name,
        ok: false,
        message: error instanceof Error ? error.message : String(error),
      });
    }
  }
  const base = {
    action,
    attempted: eligible.length,
    skipped: rows.length - eligible.length,
    succeeded,
    failures,
  };
  return { ...base, summary: describe(base) };
}
